import * as React from 'react';
import BatchFileUploadManager, {
  FileUploadInfo,
} from '@/utils/BatchFileUploadManager';
// import * as AHooks from 'ahooks';
import { FRegExpMgr } from '@/utils/FRegExpMgr';
import { FServiceAPI, FUtil } from '@freelog/tools-lib';
import * as AHooks from 'ahooks';
import { CodeSandboxCircleFilled } from '@ant-design/icons';

export enum UploadStatus {
  LOADING = 'loading', // 加载中
  PENDING = 'pending', // 等待上传
  UPLOADING = 'uploading', // 正在上传
  COMPLETED = 'completed', // 上传完成
  FAILED = 'failed', // 上传失败
  CANCELED = 'canceled', // 取消上传
  SAME_NAME = 'sameName', // 文件名相同
}

export interface ListItem {
  fileID: string;
  file: File;
  sha1: string;
  bucketName: string;
  name: string;
  status: UploadStatus;
  progress: number;
  uploadedSize: number;
  totalSize: number;
}

interface UseStorageUploadTasksResult {
  list: ListItem[]; // 列表
  successList: ListItem[]; // 成功列表
  getList: () => ListItem[]; // 获取列表
  addFiles: (
    files: { fileID: string; file: File; bucketName: string }[],
  ) => void; // 添加文件
  resumeUpload: (fileID: string, status: UploadStatus) => void; // 恢复上传
  pauseUpload: (fileID: string) => void; // 暂停上传
  // pauseAll: () => void; // 暂停所有上传
  clearAll: () => void; // 取消所有上传，清除所有任务
}

export function useStorageUploadTasks(): UseStorageUploadTasksResult {
  // 文件列表状态
  const [$list, set$List, get$List] = FUtil.Hook.useGetState<ListItem[]>([]);
  const [$toDoList, set$ToDoList, get$ToDoList] = FUtil.Hook.useGetState<
    ListItem[]
  >([]);
  const [$toDoCreateList, set$ToDoCreateList, get$ToDoCreateList] =
    FUtil.Hook.useGetState<ListItem[]>([]);
  const [$successList, set$SuccessList, get$SuccessList] =
    FUtil.Hook.useGetState<ListItem[]>([]);

  // 上传管理器引用
  const uploadManagerRef = React.useRef<BatchFileUploadManager>();
  const handleToDoList_running_Ref = React.useRef<boolean>(false);
  const handleToDoCreate_running_Ref = React.useRef<boolean>(false);

  AHooks.useMount(() => {
    // uploadManagerRef.current = BatchFileUploadManager.getInstance();
    uploadManagerRef.current = new BatchFileUploadManager();
    uploadManagerRef.current.addListenerQueueStatus((files) => {
      // console.log('uploadManagerRef.current.addListenerQueueStatus', files);
      set$List(
        get$List().map((task) => {
          const newFile = files.find((f) => f.fileID === task.fileID);
          if (newFile) {
            return {
              ...task,
              status:
                newFile.status === 'uploading'
                  ? UploadStatus.UPLOADING
                  : newFile.status === 'pending'
                  ? UploadStatus.PENDING
                  : task.status,
              progress: newFile.progress,
              uploadedSize: newFile.uploadedSize,
              totalSize: newFile.totalSize,
            };
          }
          return task;
        }),
      );
    });
    uploadManagerRef.current.addListenerUploadStatusSuccess(async (files) => {
      console.log('uploadManagerRef.current.addListenerUploadStatusSuccess &&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&', files);
      const oldToDoCreateList = get$ToDoCreateList();
      const fileIDs = files.map((f) => f.fileID);
      const needAddToDoCreateList = get$List().filter((task) =>
        fileIDs.includes(task.fileID),
      );
      set$ToDoCreateList([...oldToDoCreateList, ...needAddToDoCreateList]);
      _handleToDoCreate();
    });
  });

  AHooks.useUnmount(() => {
    uploadManagerRef.current?.clearAll();
  });

  // 添加文件
  const addFiles = React.useCallback(async function (
    tasks: { fileID: string; file: File; bucketName: string }[],
  ) {
    const newList: ListItem[] = tasks.map((task) => ({
      fileID: task.fileID,
      sha1: '',
      bucketName: task.bucketName,
      name: FRegExpMgr.resourceNameOptimized(task.file.name),
      file: task.file,
      status: UploadStatus.LOADING,
      progress: 0,
      uploadedSize: 0,
      totalSize: task.file.size,
    }));
    const oldList = get$List();
    set$List([...oldList, ...newList]);
    const $toDoList = get$ToDoList();
    set$ToDoList([...$toDoList, ...newList]);
    _handleToDoList();
  },
  []);

  async function _handleToDoList() {
    if (handleToDoList_running_Ref.current) {
      return;
    }
    handleToDoList_running_Ref.current = true;
    while (get$ToDoList().length > 0) {
      const [task, ...rest] = get$ToDoList();

      const params1: Parameters<typeof FServiceAPI.Storage.batchObjectList>[0] =
        {
          fullObjectNames: task.bucketName + '/' + task.name,
          projection: 'objectId,objectName',
        };
      const { data: data_batchObjectList } =
        await FServiceAPI.Storage.batchObjectList(params1);
      if (data_batchObjectList.length > 0) {
        // 已存在同名
        set$List(
          get$List().map((item) => {
            if (item.fileID === task.fileID) {
              return {
                ...item,
                status: UploadStatus.SAME_NAME,
              };
            }
            return item;
          }),
        );
      } else {
        await _handleUpload(task.fileID);
      }
      // set$ToDoList(rest);
      set$ToDoList(get$ToDoList().filter((t) => t.fileID !== task.fileID));
    }
    handleToDoList_running_Ref.current = false;
  }

  async function _handleToDoCreate() {
    if (handleToDoCreate_running_Ref.current) {
      return;
    }
    handleToDoCreate_running_Ref.current = true;
    while (get$ToDoCreateList().length > 0) {
      const [task, ...rest] = get$ToDoCreateList();
      const params: Parameters<typeof FServiceAPI.Storage.createObject>[0] = {
        bucketName: task.bucketName,
        objectName: task.name,
        sha1: task.sha1,
      };
      await FServiceAPI.Storage.createObject(params);
      const list = get$List().map((t) => {
        if (t.fileID === task.fileID) {
          return {
            ...t,
            status: UploadStatus.COMPLETED,
          };
        }
        return t;
      });
      set$List(list);
      set$SuccessList([task]);
      set$ToDoCreateList(
        get$ToDoCreateList().filter((t) => t.fileID !== task.fileID),
      );
    }
    handleToDoCreate_running_Ref.current = false;
  }

  async function _handleUpload(fileID: string) {
    const task = get$List().find((t) => t.fileID === fileID);
    if (!task) {
      return;
    }
    const sha1 = await FUtil.Tool.getSHA1Hash(task.file);
    const params0: Parameters<typeof FServiceAPI.Storage.fileIsExist>[0] = {
      sha1,
    };
    const { data: data_fileIsExist } = await FServiceAPI.Storage.fileIsExist(
      params0,
    );
    if (data_fileIsExist[0].isExisting) {
      set$List(
        get$List().map((item) => {
          if (item.fileID === task.fileID) {
            return { ...item, status: UploadStatus.COMPLETED, sha1 };
          }
          return item;
        }),
      );
      const params: Parameters<typeof FServiceAPI.Storage.createObject>[0] = {
        bucketName: task.bucketName,
        objectName: task.name,
        sha1: sha1,
      };
      await FServiceAPI.Storage.createObject(params);
      set$SuccessList([task]);
    } else {
      // const sha1 = await FUtil.Tool.getSHA1Hash(task.file);
      set$List(
        get$List().map((item) => {
          if (item.fileID === task.fileID) {
            return { ...item, status: UploadStatus.PENDING, sha1 };
          }
          return item;
        }),
      );
      uploadManagerRef.current?.addFiles([
        {
          fileID: task.fileID,
          file: task.file,
        },
      ]);
    }
  }

  // 恢复上传
  const resumeUpload = React.useCallback(async function (
    fileID: string,
    status: UploadStatus,
  ) {
    set$List(
      get$List().map((item) => {
        if (item.fileID === fileID) {
          return { ...item, status: UploadStatus.LOADING };
        }
        return item;
      }),
    );

    const task = get$List().find((t) => t.fileID === fileID);
    // console.log('resumeUpload', task);
    if (task) {
      if (status === UploadStatus.CANCELED) {
        await uploadManagerRef.current?.resumeUpload(fileID);
      } else if (status === UploadStatus.SAME_NAME) {
        await _handleUpload(fileID);
      } else if (status === UploadStatus.FAILED) {
        // await _handleUpload(fileID);
      }
    }
  },
  []);

  // 取消上传
  const pauseUpload = React.useCallback(async function (fileID: string) {
    await uploadManagerRef.current?.pauseUpload(fileID);
    set$List(
      get$List().map((item) => {
        if (item.fileID === fileID) {
          return { ...item, status: UploadStatus.CANCELED };
        }
        return item;
      }),
    );
  }, []);
  // 取消所有上传
  const clearAll = React.useCallback(() => {
    // uploadManagerRef.current?.clearAll();
    set$ToDoList([]);
    set$ToDoCreateList([]);
    set$SuccessList([]);
    set$List([]);
    uploadManagerRef.current?.clearAllData();
  }, []);

  return {
    list: $list,
    successList: $successList,
    getList: get$List,
    addFiles,
    resumeUpload,
    pauseUpload,
    clearAll,
  };
}

// 映射上传状态
// function mapUploadStatus(status: string): UploadStatus {
//   switch (status) {
//     case 'loading':
//       return UploadStatus.LOADING;
//     case 'pending':
//       return UploadStatus.PENDING;
//     case 'uploading':
//       return UploadStatus.UPLOADING;
//     case 'completed':
//       return UploadStatus.COMPLETED;
//     case 'canceled':
//       return UploadStatus.CANCELED;
//     case 'sameName':
//       return UploadStatus.SAME_NAME;
//     case 'failed':
//       return UploadStatus.FAILED;
//     default:
//       return UploadStatus.PENDING;
//   }
// }
